/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import './Filter.scss'
import Loading from '../Loading/Loading';
import FilterConditions from '../FilterConditions/FilterConditions';
import React, { FC, forwardRef, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { ICondition, ITagAttributeAllowedValueItem, ITagAttributeAllowedValue, ITagAttribute, IFilter as IExploreFilter } from 'store/sidebar/types';
import { GetTagAttributeAllowedValuesThunk, GetTagAttributeInfoThunk } from '../../store/sidebar/thunk';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import useLicenseAccess from '../../hooks/useLicenseAccess';
import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import { workspaceSlice } from 'store/workspace/slice';
import { analogueFilterOptionsSlice } from 'store/analogueFilterOptions/slice';
import { analogueSlice } from 'store/analogue/slice';
import { v4 as uuidv4 } from 'uuid';
import { ITreeElement } from 'helpers/types/tree';
import { Button } from '@progress/kendo-react-buttons';
import { trackGAEvent } from 'helpers/functions/GoogleAnalytics/GA';
import { isReadOnlyAccess, getWorkspaceOwnerEmail } from 'helpers/functions/workspaceHelpers';
import { selectActiveUser } from 'store/userState/selectors';
import { eventsGA } from 'helpers/functions/GoogleAnalytics/types';
import { selectAllTagAttributes } from "store/sidebar/selectors"
import { filterIcon } from "@progress/kendo-svg-icons" 
import { requestDeduplicationService } from "helpers/functions/requestDeduplication"

// Memoized FilterCheckbox component to prevent unnecessary re-renders
const FilterCheckbox = React.memo(({ 
  value, 
  isChecked, 
  onChange 
}: { 
  value: ITagAttributeAllowedValueItem, 
  isChecked: boolean, 
  onChange: (e: CheckboxChangeEvent) => void 
}) => {
  return (
    <div className="filterCheckboxBlock">
      <Checkbox
        checked={isChecked}
        onChange={onChange}
      />
      <label>{value.text}</label>
    </div>
  );
});

FilterCheckbox.displayName = 'FilterCheckbox';

interface IProps {
  props: ITreeElement;
  modalOnly?: boolean;
  mode?: 'explore' | 'analogue';
  onFilterApplied?: (updatedFilters?: any[]) => void; // Modified to accept updated filters
  disabled?: boolean; // Whether the filter is disabled due to license restrictions
}

type FilterRef = {
    handleFilterButton: (e: React.MouseEvent) => void; // Specify the event type, here it is MouseEvent
};

const Filter = forwardRef<FilterRef, IProps>((props, ref) => {
    const { props: item, modalOnly = false, mode = 'explore', onFilterApplied, disabled = false } = props;
    const dispatch = useAppDispatch();
    
    // License access control for filtering
    const { checkDatasetAccess, blockUnauthorizedAction } = useLicenseAccess();

    // Mode-specific selectors
    const workspaceFilters = useAppSelector((state) => state.workspaceSlice.filters.filterOptions);
    const analogueFilters = useAppSelector((state) => state.analogueFilterOptionsSlice.filters.filterOptions);
    
    const { loadAllowedValues, tagAttributeAllowedValues, workspaceId, dropdownAllTagAttributeValues } = useAppSelector(
        (state) => state.workspaceSlice
    );
    
    
    const { analoguePlotList, workSpaceIndex } = useAppSelector((state) => state.analogueSlice);
    const analogueWorkspaceId = analoguePlotList[workSpaceIndex]?.id || '';

    const { filters: workspaceFiltersFilters } = workspaceFilters;
    const { filters: analogueFiltersFilters } = analogueFilters;
    
    // Read-only workspace checking
    const currentUser = useAppSelector(selectActiveUser);
    const workspaces = useAppSelector((state) => state.workspaceSlice.workspaces);
    const currentWorkspace = useMemo(() => {
        const wsId = mode === 'explore' ? workspaceId : analogueWorkspaceId;
        return workspaces.find(ws => ws.id === wsId) || null;
    }, [workspaces, workspaceId, analogueWorkspaceId, mode]);

    const [open, setOpen] = useState<boolean>(false);
    const [bottom, setBottom] = useState<boolean>(false);
    const [firstConditionInputValue, setFirstConditionInputValue] = useState<string>('');
    const [secondConditionInputValue, setSecondConditionInputValue] = useState<string>('');
    const [firstConditionListValue, setFirstConditionListValue] = useState<ICondition>({ id: 3, name: 'Contains', defaultPriority: 1 });
    const [secondConditionListValue, setSecondConditionListValue] = useState<ICondition>({ id: 5, name: 'Is contained In', defaultPriority: null });
    const [logicalOperationListValue, setLogicalOperationListValue] = useState<ICondition>({ id: 1, name: 'And', defaultPriority: 1 });
    const [filterName, setFilterName] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isValuesFetching, setIsValuesFetching] = useState<boolean>(false);

    // Local state to track checkbox selections (not applied to global state until Filter button is clicked)
    const [localCheckedValues, setLocalCheckedValues] = useState<Set<string>>(new Set());
    
    // Local state to track if filter is applied for immediate UI feedback
    const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);

    const filtersSearchText = useAppSelector((store) => store.sideBarTreeSlice.filtersSearchText);
    const filtersSearched = !!filtersSearchText;

    const filtersDropDownRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const filterButtonRef = useRef() as React.MutableRefObject<any>;
    const tagAttributes = useAppSelector(selectAllTagAttributes);

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const scrollPositionRef = useRef<number>(0);
    const prevOpenStateRef = useRef<boolean>(false);
    
    // Add a flag to track if we should preserve scroll position
    const shouldPreserveScrollRef = useRef<boolean>(false);

    // Use correct filters based on mode - memoize this
    const exploreFilters = useMemo(() => workspaceFiltersFilters, [workspaceFiltersFilters]);
    const currentFilters = useMemo(() => mode === 'analogue' ? analogueFiltersFilters : workspaceFiltersFilters, [mode, analogueFiltersFilters, workspaceFiltersFilters]);
    // Use workspace loadAllowedValues for both modes
    
    // Memoize the current workspace ID to prevent unnecessary re-renders
    const currentWorkspaceId = useMemo(() => {
        return mode === 'analogue' ? analogueWorkspaceId : workspaceId;
    }, [mode, analogueWorkspaceId, workspaceId]);

    // Type guard for explore filter
    function isExploreFilter(filter: any): filter is IExploreFilter {
        return Array.isArray(filter?.values);
    }

    // Load existing filter values when opening filter
    const convertOption = useCallback(() => {
        // Check if there's an existing filter for this attribute
        const existingFilter = currentFilters.find(f => f.tagAttributeId === item?.item.id);
        
        // Initialize local checkbox state from current applied filters
        if (existingFilter && isExploreFilter(existingFilter)) {
            setLocalCheckedValues(new Set(existingFilter.values || []));
        } else {
            setLocalCheckedValues(new Set());
        }
        
        // Load saved values from item
        setFirstConditionInputValue(item?.item?.firstConditionInput || '');
        setSecondConditionInputValue(item?.item?.secondConditionInput || '');
        
        // Determine if this is a numerical field
        const tagAttribute = tagAttributes.find((f: ITagAttribute) => f.id === item?.item.id);
        const isNumericField = tagAttribute?.unitOfMeasurement && tagAttribute.unitOfMeasurement !== 'TEXT';
        // Default values for condition lists - different for numeric vs text fields
        const defaultFirstCondition = isNumericField 
            ? { id: 5, name: 'Is greater than', defaultPriority: 1 }
            : { id: 3, name: 'Contains', defaultPriority: 1 };
        const defaultSecondCondition = isNumericField
            ? { id: 3, name: 'Is less than', defaultPriority: null }
            : { id: 5, name: 'Is contained In', defaultPriority: null };
        const defaultLogicalOperation = isNumericField
            ? { id: 1, name: 'And', defaultPriority: 1 }
            : { id: 7, name: 'Is equal to', defaultPriority: null };
        
        // If filter exists, load values from it
        if (existingFilter) {
            // Load input values from existing filter if they exist
            if (existingFilter.partOne?.value) {
                setFirstConditionInputValue(existingFilter.partOne.value);
            }
            
            if (existingFilter.partTwo?.value) {
                setSecondConditionInputValue(existingFilter.partTwo.value);
            }
        }
        // Set condition list values, prioritizing: saved item values > existing filter values > defaults
        setFirstConditionListValue(
            item?.item?.firstConditionList || 
            (existingFilter?.partOne?.operationId ? 
                { 
                    id: existingFilter.partOne.operationId || defaultFirstCondition.id, 
                    name: existingFilter.partOne.operationNameString || existingFilter.partOne.operationNameNumeric || defaultFirstCondition.name,
                    defaultPriority: defaultFirstCondition.defaultPriority
                } : defaultFirstCondition)
        );
        setSecondConditionListValue(
            item?.item?.secondConditionList || 
            (existingFilter?.partTwo?.operationId ? 
                { 
                    id: existingFilter.partTwo.operationId, 
                    name: existingFilter.partTwo.operationNameString || existingFilter.partTwo.operationNameNumeric || defaultSecondCondition.name,
                    defaultPriority: null
                } : defaultSecondCondition)
        );
        
        setLogicalOperationListValue(
            item?.item?.logicalOperationList || 
            (existingFilter?.booleanOperationId ? 
                { 
                    id: existingFilter.booleanOperationId, 
                    name: defaultLogicalOperation.name,  // Use the default name
                    defaultPriority: null
                } : defaultLogicalOperation)
        );
    }, [currentFilters, item?.item?.id, item?.item?.firstConditionInput, item?.item?.secondConditionInput, item?.item?.firstConditionList, item?.item?.secondConditionList, item?.item?.logicalOperationList, tagAttributes]);

    const handleFilterButton = useCallback((e: any) => {
        // Prevent filter functionality if disabled due to license restrictions
        if (disabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        
        // Call convertOption to update state values before opening the filter
        convertOption();
        
        setIsValuesFetching(true);
        
        // Always use the workspace tagAttributeAllowedValues for both modes
        dispatch(GetTagAttributeAllowedValuesThunk({ 
            tagAttributeId: item?.item.id, 
            filters: mode === 'analogue' ? analogueFiltersFilters : exploreFilters, 
            workspaceId: workspaceId, 
            props: item,
        })).finally(() => {
            setIsValuesFetching(false);
        });
        
        e.screenY < 690 ? setBottom(true) : setBottom(false);
        
        // When called from BroadComponent, just set the open state
        if (mode === 'analogue' && modalOnly) {
            setOpen(true);
            dispatch(analogueSlice.actions.setFilterMode({ value: "analogue" }))
        }
        
        const updatedFilters = [...currentFilters];
        const existingFilterIndex = updatedFilters.findIndex(f => f.tagAttributeId === item?.item.id);
        
        if (existingFilterIndex >= 0) {
            updatedFilters[existingFilterIndex] = {
                ...updatedFilters[existingFilterIndex],
                values: Array.from(localCheckedValues)
            };
        } else if (localCheckedValues.size > 0) {
            updatedFilters.push({
                tagAttributeId: item?.item.id ?? '',
                values: Array.from(localCheckedValues),
                partOne: {
                    value: '',
                    ignoreCase: true,
                    operationId: null,
                    operationNameNumeric: null,
                    operationNameString: null,
                },
                booleanOperationId: null,
                partTwo: {
                    value: '',
                    ignoreCase: true,
                    operationId: null,
                    operationNameNumeric: null,
                    operationNameString: null,
                }
            });
        }
        
        setIsValuesFetching(true);
        
        // Toggle open state after all other operations
        setOpen(!open);
        prevOpenStateRef.current = !open;
        
        // Reset scroll position when opening filter
        if (!open) {
            scrollPositionRef.current = 0;
        }
    }, [disabled, open, item?.item?.id, localCheckedValues, dispatch, currentWorkspaceId, mode, modalOnly, currentFilters, convertOption]); // Use memoized workspace ID

    // Initialize filter applied state based on existing filters
    useEffect(() => {
        const existingFilter = currentFilters.find(f => f.tagAttributeId === item?.item.id);
        const hasFilter = existingFilter && (
            (existingFilter.values && existingFilter.values.length > 0) ||
            existingFilter.partOne?.value ||
            existingFilter.partTwo?.value
        );
        setIsFilterApplied(!!hasFilter || !!item?.item?.filterApplied);
    }, [currentFilters, item?.item?.id, item?.item?.filterApplied]);

    useEffect(() => {
        convertOption()
        prevOpenStateRef.current = open;
    }, [open, item, currentFilters, convertOption]);

    // Modified effect to only restore scroll position when needed and avoid jumping to top
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer && shouldPreserveScrollRef.current) {
            // Use setTimeout to ensure the DOM has updated before setting scroll position
            setTimeout(() => {
                if (scrollContainer) {
                    scrollContainer.scrollTop = scrollPositionRef.current;
                }
            }, 0);
            shouldPreserveScrollRef.current = false; // Reset the flag
        }
    }, [tagAttributeAllowedValues]);

    // Modified checkbox handler to properly manage scroll position
    const handleCheckbox = useCallback(
        (e: CheckboxChangeEvent, filter: ITagAttributeAllowedValueItem, id: string) => {
            // Save current scroll position before state change
            if (scrollContainerRef.current) {
                scrollPositionRef.current = scrollContainerRef.current.scrollTop;
                shouldPreserveScrollRef.current = true; // Set flag to preserve scroll
            }
            
            const newCheckedValues = new Set(localCheckedValues);
            if (e.value) {
                newCheckedValues.add(filter.text);
            } else {
                newCheckedValues.delete(filter.text);
            }
            
            // Update state and immediately restore scroll position
            setLocalCheckedValues(newCheckedValues);
            
            // Use requestAnimationFrame to ensure the update happens after the render
            requestAnimationFrame(() => {
                if (scrollContainerRef.current && scrollPositionRef.current > 0) {
                    scrollContainerRef.current.scrollTop = scrollPositionRef.current;
                }
            });
        },
        [disabled, localCheckedValues]
    );

    // Optimize applyFilter with useCallback
    const applyFilter = useCallback(() => {
        // Prevent filtering if disabled due to license restrictions
        if (disabled) {
            return;
        }
        
        // Check for read-only workspace access
        if (isReadOnlyAccess(currentWorkspace, currentUser?.id)) {
            const ownerEmail = getWorkspaceOwnerEmail(currentWorkspace);
            const message = ownerEmail 
                ? `This is a read-only workspace, and therefore cannot be edited.\n\nThis workspace is owned by ${ownerEmail}`
                : 'This is a read-only workspace, and therefore cannot be edited.';
            dispatch(workspaceSlice.actions.handleRestrictionPopup({ open: true, message }));
            return;
        }
        
        // Check if user has permission to filter this dataset
        if (item.item.id && !checkDatasetAccess(item.item.id).canFilter) {
            console.warn(`Unauthorized filter attempt for dataset: ${item.item.name}`);
            return;
        }
        if (!item?.item?.id) {
            console.error('Invalid item or item.id in applyFilter');
            return;
        }
                
        const checkedValuesArray = Array.from(localCheckedValues);
        const updatedFilters = [...currentFilters];
        const existingFilterIndex = updatedFilters.findIndex(f => f.tagAttributeId === item.item.id);
        
        // Create filter parts with actual condition values
        const partOne = {
            value: firstConditionInputValue || '',
            ignoreCase: true,
            operationId: firstConditionListValue?.id || null,
            // Store name in both fields to ensure compatibility
            operationNameNumeric: firstConditionListValue?.name || null,
            operationNameString: firstConditionListValue?.name || null
        };
        
        const partTwo = {
            value: secondConditionInputValue || '',
            ignoreCase: true,
            operationId: secondConditionListValue?.id || null,
            // Store name in both fields to ensure compatibility
            operationNameNumeric: secondConditionListValue?.name || null,
            operationNameString: secondConditionListValue?.name || null
        };
        
        if (existingFilterIndex >= 0) {
            updatedFilters[existingFilterIndex] = {
                ...updatedFilters[existingFilterIndex],
                values: checkedValuesArray,
                partOne,
                booleanOperationId: logicalOperationListValue?.id || null,
                partTwo
            };
        } else if (checkedValuesArray.length > 0 || firstConditionInputValue || secondConditionInputValue) {
            updatedFilters.push({
                tagAttributeId: item?.item.id ?? '',
                values: checkedValuesArray,
                partOne,
                booleanOperationId: logicalOperationListValue?.id || null,
                partTwo
            });
        }
        
        // Determine if filter is applied
        const isFilterApplied = checkedValuesArray.length > 0 || !!firstConditionInputValue || !!secondConditionInputValue;
        
        if (mode === 'analogue') {
            dispatch(analogueFilterOptionsSlice.actions.setAnalogueFilterOptionItem({ 
                element: 'filters', 
                value: updatedFilters 
            }));
        } else {
            dispatch(workspaceSlice.actions.setFilterOptionItem({ 
                element: 'filters', 
                value: updatedFilters 
            }));
        }
        const updatedItem = {
            ...item.item,
            values: checkedValuesArray,
            firstConditionInput: firstConditionInputValue,
            secondConditionInput: secondConditionInputValue,
            firstConditionList: firstConditionListValue,
            secondConditionList: secondConditionListValue,
            logicalOperationList: logicalOperationListValue,
            filterApplied: isFilterApplied
        };
        
        const filterPayload = {
            props: {
                item: {
                    ...item,
                    item: {
                        ...updatedItem,
                        id: item.item.id,
                        name: item.item.name,
                        itemHierarchicalIndex: item.itemHierarchicalIndex || ''
                    }
                }
            },
            filtersSearched
        };
        
        setIsFilterApplied(isFilterApplied);
        setIsValuesFetching(true);
        
        if (mode === 'analogue') {
            dispatch(analogueFilterOptionsSlice.actions.applyAnalogueFilter(filterPayload));
        } else {
            dispatch(workspaceSlice.actions.applyFilter(filterPayload));
        }
            
        dispatch(GetTagAttributeAllowedValuesThunk({ 
                tagAttributeId: item.item.id, 
                filters: updatedFilters, 
                workspaceId: workspaceId, 
                props: item 
            })).finally(() => {
                setIsValuesFetching(false);
            }); 
        
        setOpen(false);
        
        const eventObj: any = { filter: item?.item.name, value: checkedValuesArray.join() };
        let filterLogicalCondition = '';
        
        if (firstConditionInputValue) {
            filterLogicalCondition = `${firstConditionListValue?.name || ''} ${firstConditionInputValue}`;
        }
        
        if (firstConditionInputValue && secondConditionInputValue) {
            filterLogicalCondition += ` ${logicalOperationListValue?.name || ''}`;
        }
        
        if (secondConditionInputValue) {
            filterLogicalCondition += ` ${secondConditionListValue?.name || ''} ${secondConditionInputValue}`;
        }
        
        if (filterLogicalCondition) {
            eventObj.filterLogicalCondition = filterLogicalCondition;
        }

        if (onFilterApplied) {
            onFilterApplied(updatedFilters);
        }

        trackGAEvent(eventsGA.filtered, eventObj);
    }, [
        item?.item?.id,
        item?.item?.name,
        mode,
        currentFilters,
        localCheckedValues,
        firstConditionInputValue,
        secondConditionInputValue,
        firstConditionListValue,
        secondConditionListValue,
        logicalOperationListValue,
        filtersSearched,
        dispatch,
        currentWorkspaceId,
        onFilterApplied,
        currentWorkspace,
        currentUser?.id,
        disabled,
        checkDatasetAccess
    ]); 

    const getFilters = async () => {
        setTimeout(async () => {
            await dispatch(GetTagAttributeAllowedValuesThunk({ 
                tagAttributeId: item?.item.id, 
                filters: exploreFilters, 
                workspaceId: workspaceId, 
                props: item,
                // mode: mode || 'explore'
            }));
        }, 200);
    };

    const clearFilter = async () => {
        const tagAttribute = tagAttributes.find((f: ITagAttribute) => f.id === item?.item.id);
        if (mode === 'analogue') {
            await dispatch(analogueFilterOptionsSlice.actions.clearAnalogueFilter({ ...props, item, filtersSearched, tagAttribute }));
        } else {
            await dispatch(workspaceSlice.actions.clearFilter({ ...props, item, filtersSearched, tagAttribute }));
        }
        
        // Reset local state as well
        setLocalCheckedValues(new Set());
        setIsFilterApplied(false);
        await getFilters();
        
        // Create updated filters after clearing this specific filter
        const updatedFilters = currentFilters.filter(f => f.tagAttributeId !== item?.item.id);
        
        if (onFilterApplied) {
            onFilterApplied(updatedFilters);
        }
        prevOpenStateRef.current = !open;
        setOpen(false);
    };

    React.useImperativeHandle(ref, () => ({
        handleFilterButton,
    }), [handleFilterButton]);

    useEffect(() => {
        const handleClickOutside = (event: any) => {
        if (
            filtersDropDownRef.current &&
            !filtersDropDownRef.current.contains(event.target) &&
            filterButtonRef.current &&
            !filterButtonRef.current.element.contains(event.target) &&
            !event.target.classList.contains('k-list-item-text') &&
            !event.target.classList.contains('k-list-item')
        ) {
            setOpen(false);
        }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [filtersDropDownRef]);

    const inputs = useMemo(() => ({
        firstConditionInputValue,
        setFirstConditionInputValue,
        secondConditionInputValue,
        setSecondConditionInputValue,
        firstConditionListValue,
        setFirstConditionListValue,
        secondConditionListValue,
        setSecondConditionListValue,
        logicalOperationListValue,
        setLogicalOperationListValue,
    }), [
        firstConditionInputValue,
        setFirstConditionInputValue,
        secondConditionInputValue,
        setSecondConditionInputValue,
        firstConditionListValue,
        setFirstConditionListValue,
        secondConditionListValue,
        setSecondConditionListValue,
        logicalOperationListValue,
        setLogicalOperationListValue,
    ]);

    const tagAttribute = tagAttributes.find((f: ITagAttribute) => f.id === item?.item.id);
    const unit = tagAttribute?.unitOfMeasurement || '';

    useEffect(() => {
        setIsLoading(true);
        
        // Only get tag info when component loads (for expand functionality)
        dispatch(
            GetTagAttributeInfoThunk({
                tagAttributeId: item?.item.id, 
                workspaceId: currentWorkspaceId, 
                props: item
            })
        ).then((res) => {
            setIsLoading(false);
            
            // Check if filter is already applied when component loads (without fetching values)
            const existingFilter = exploreFilters.find(f => f.tagAttributeId === item?.item.id);
            const hasFilter = existingFilter && (
                (existingFilter.values && existingFilter.values.length > 0) ||
                existingFilter.partOne?.value ||
                existingFilter.partTwo?.value
            );
            setIsFilterApplied(!!hasFilter || !!item?.item?.filterApplied);
        }).catch((error) => {
            setIsLoading(false);
        });
    }, [item?.item.id, currentWorkspaceId, dispatch, exploreFilters, item?.item?.filterApplied]);

    // Cleanup effect
    useEffect(() => {
        return () => {
            requestDeduplicationService.clearAll()
        }
    }, [])

    if (isLoading) {
        return <div></div>;
    }

    return (
        <>
        <div className={item?.item.type}>
            {!modalOnly && !filterName.includes(item?.item.id) ? (
            <div
                className="titleContainer"
                title={`${item?.item.tooltip || ''}${unit ? ` [${unit}]` : ''}`}
            >
                <span className="title">
                {item?.item.name} {unit ? `(${unit})` : ''}
                </span>
                <Button
                    svgIcon={filterIcon}
                    themeColor={isFilterApplied ? 'primary' : 'light'}
                    disabled={disabled}
                    style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
                    onClick={handleFilterButton}
                    ref={filterButtonRef}
                />
            </div>
            ) : null}
            <div
                className={open ? `FiltersDropDown ${bottom && 'bottom'}` : `FiltersDropDown closed ${!bottom && 'false'}`}
                ref={filtersDropDownRef}
                onClick={(e) => e.preventDefault()}
            >
            <div className="checkboxes" ref={scrollContainerRef}>
                {loadAllowedValues || isValuesFetching ? (
                <Loading />
                ) : (
                tagAttributeAllowedValues
                    .find((f: ITagAttributeAllowedValue ) => f.tagId === item?.item.id)
                    ?.values.map((val: ITagAttributeAllowedValueItem ) => {
                    const isChecked = localCheckedValues.has(val.text);
                    
                    return (
                        <FilterCheckbox
                            key={uuidv4()}
                            value={val}
                            isChecked={isChecked || val.checked}
                            onChange={(e) => handleCheckbox(e, val, item?.item.id)}
                        />
                    );
                })
                )}
            </div>
            <div className="filterInputsBlock">
                <h3>Show rows with a value that</h3>
                <FilterConditions props={item} inputs={inputs} />
                <div className="actions">
                <Button themeColor="primary" className="filter" onClick={applyFilter}>
                    Filter
                </Button>
                <Button themeColor="primary" className="clearFilter" onClick={clearFilter}>
                    Clear Filter
                </Button>
                </div>
            </div>
            </div>
        </div>
        </>
    );
});
  
  Filter.displayName = 'Filter';

export default React.memo(Filter);